import { useTranslation } from 'react-i18next'
import type { DirectoryUser } from '@/api/directory'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { userLabel } from '@/lib/userDisplay'

/**
 * Who works a lead, as a name.
 *
 * The list only carries the id, and the name comes from the colleague directory — one request for
 * the whole page rather than a name per row. Until it has arrived the cell stays empty rather than
 * showing an id nobody can read; once it has, an id it does not know is a deleted account, since
 * the directory holds everybody else.
 *
 * An account that can no longer log in is marked: a lead assigned to it is one nobody is working,
 * and this is where somebody would notice.
 */
export function AssigneeName({
  assigneeId,
  colleagues,
  loaded,
}: {
  assigneeId: string | null
  colleagues: DirectoryUser[]
  /** Whether the directory has arrived; before that, nobody is "unknown" yet. */
  loaded: boolean
}) {
  const { t } = useTranslation()

  if (assigneeId === null) {
    return <span className="text-muted-foreground">{t('leads.assignee.nobody')}</span>
  }
  if (!loaded) {
    return null
  }
  const colleague = colleagues.find((one) => one.id === assigneeId)
  if (!colleague) {
    return <span className="text-muted-foreground">{t('leads.assignee.unknown')}</span>
  }
  return (
    <span className="flex min-w-0 items-center gap-2">
      <span className="truncate">{userLabel(colleague, colleague.id)}</span>
      {!colleague.assignable && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant="outline" className="text-muted-foreground shrink-0 font-normal">
              {t('leads.assignee.inactive')}
            </Badge>
          </TooltipTrigger>
          <TooltipContent>{t('leads.assignee.inactiveHint')}</TooltipContent>
        </Tooltip>
      )}
    </span>
  )
}
